/**
 * Debounce de quick-view preload-on-hover.
 *
 * Het thema bundle.js registreert op elke product-card een `mouseenter`-
 * listener die direct `callQuickView(false)` aanroept → fetch /products/X
 * + parseHTML. Op desktop, als de gebruiker met de muis langs een
 * collectie scrolt, vuurt dat 10-20+ fetches tegelijk af. Main thread
 * raakt overbelast → de CSS-transforms voor image-zoom en de "KOOP NU"-
 * button slide stutteren, klikken voelt laggy, openen van de drawer
 * voelt traag.
 *
 * Deze patch wrappert `callQuickView` op de prototype van de
 * product-card custom elements: hover-preload (withOpen=false) wordt
 * 200ms gedebounced. Echte klikken (withOpen=true) firen direct —
 * geen latency op de gewenste actie.
 *
 * Effect: bij snel langs cards bewegen vuurt 0 fetches af i.p.v. 20.
 * Main thread blijft vrij → animaties blijven 60fps.
 */
(function () {
  'use strict';

  /* Korte hover-dwell (100ms) is genoeg om "snel langs glijden" vs
     "doelgericht stilhouden op een card" te onderscheiden. Lager dan
     dit voelt vluchtige passages al als intentie; hoger maakt
     doelgericht hover-en traag aanvoelen op het moment van klik. */
  var DWELL_MS = 100;
  var timers = new WeakMap();
  var CARD_SELECTOR = 'product-card, horizontal-product-card, vertical-product-card';

  /* Per-card pointerdown handler. Vervangt een eerdere document-level
     capture-phase listener die op élke pointerdown op de pagina (ook
     scroll-starts en clicks op header/footer/checkout) een .closest()-
     walk deed — meetbare INP-impact op Shopify's Web Performance
     rapport. Nu fires alleen wanneer pointerdown daadwerkelijk op een
     product-card landt, dus nul overhead elders. */
  function bindPointerdown(card) {
    if (card.__cnPointerdownBound) return;
    card.__cnPointerdownBound = true;
    card.addEventListener('pointerdown', function () {
      var origFn = card.__cnOrigCallQuickView;
      if (typeof origFn !== 'function') return;
      var existing = timers.get(card);
      if (existing) {
        clearTimeout(existing);
        timers.delete(card);
      }
      try { origFn.call(card, false); } catch (err) { /* ignore */ }
    }, { passive: true });
  }

  function patchPrototype(TagClass, tagName) {
    if (!TagClass || !TagClass.prototype) return;
    var proto = TagClass.prototype;
    if (typeof proto.callQuickView !== 'function') return;
    if (proto.__cnDebouncedQv) return; // idempotent
    proto.__cnDebouncedQv = true;

    var orig = proto.callQuickView;
    proto.__cnOrigCallQuickView = orig;
    proto.callQuickView = function (withOpen) {
      var self = this;
      var existing = timers.get(self);
      if (existing) {
        clearTimeout(existing);
        timers.delete(self);
      }

      if (withOpen) {
        // Echte klik op KOOP NU / quick-view button → direct firen.
        return orig.call(self, true);
      }

      // Hover-preload → wachten tot de gebruiker écht dwell't.
      var t = setTimeout(function () {
        timers.delete(self);
        try { orig.call(self, false); } catch (e) { /* ignore */ }
      }, DWELL_MS);
      timers.set(self, t);
    };

    /* Hook connectedCallback zodat nieuwe instances (lazy-loaded,
       infinite-scroll, AJAX re-render) automatisch de pointerdown
       binding krijgen. */
    var origConnected = proto.connectedCallback;
    proto.connectedCallback = function () {
      if (typeof origConnected === 'function') {
        try { origConnected.call(this); } catch (e) { /* ignore */ }
      }
      bindPointerdown(this);
    };

    /* Already-connected instances missen de connectedCallback-hook
       hierboven (die slaat hun eerste connect over). Bind handmatig. */
    document.querySelectorAll(tagName).forEach(bindPointerdown);
  }

  // Bekend product-card custom-element-namen in dit thema. Allemaal
  // delen ze de callQuickView API; we patchen ze stuk voor stuk zodra
  // ze gedefinieerd zijn.
  ['product-card', 'horizontal-product-card', 'vertical-product-card'].forEach(function (name) {
    if (!('customElements' in window)) return;
    customElements.whenDefined(name).then(function () {
      patchPrototype(customElements.get(name), name);
    }).catch(function () { /* element bestaat niet op dit thema → skip */ });
  });
})();
